import { mapConfig } from 'lib/config'
import { Position, serializeCompactV0 } from 'lib/place'
import {
  isAddressItem,
  isQueryItem,
  isQueryType,
  MediaEditorialsItem,
  MediaImagesItem,
  MediaRatingsItem,
  SearchResultEntityItem,
  SearchServiceResultItem,
} from 'map/services'
import { calculateAndFormatDistance, CountryAndUnit } from 'utils/formatters'
import { RecentItem, RecentItemType, SerializedRecentItem } from 'utils/storage'

import { Coordinates } from './index'

// TODO Need to resolve the types here and make more specific state oriented types
// At the moment there are types are coming from map/service and those are part of state properties.
// The problem with this approach is that when a state is consumed by the components will create a coupling with map/service just to read the data,
// which is not idiomatic
export enum SearchStateListType {
  Autosuggest = 'autosuggest',
  Search = 'search',
  Recent = 'recent',
}

export interface SearchState {
  searchItemList: SearchStateListItem[]
  autosuggestList: SearchStateListItem[]
  browseList: SearchStateListItem[]
  recentItemList: SearchStateListItem[]
  locationAddress: SearchStateListItem[]
  query: string
  lastUpdatedList?: SearchStateListType
  isLoading: boolean
  error?: string
}

export interface SearchStateListItem {
  id: string
  name: string
  resultType: string
  title: string
  urlPath: string
  subtitle?: string
  iconName?: string
  categoryId?: string
  distanceLabel?: string
  thumbnail?: string
  averageRating?: number
  reviewCount?: number
  ratingLink?: string
  address?: {
    label: string
    street: string
    houseNumber: string
  }
  distance?: number
  position?: {
    lat: number
    lng: number
  }
  categories?: {
    id: string
    name: string
    primary?: true
  }[]
  contacts?: {
    email?: {
      value: string
    }[]
    phone?: {
      value: string
    }[]
    www?: {
      value: string
    }[]
  }[]
  openingHours?: {
    isOpen: boolean
    text: string[]
  }[]
  media?: {
    images?: {
      items: MediaImagesItem[]
    }
    ratings?: {
      items: MediaRatingsItem[]
    }
    editorials?: {
      items: MediaEditorialsItem[]
    }
  }
}

export const fromSearchServiceResultItemToSearchStateListItem = (
  item: SearchServiceResultItem,
  countryAndUnit: CountryAndUnit,
  userCurrentPosition: Coordinates | null
): SearchStateListItem => {
  const id = item.id
  const iconName = isAddressItem(item) ? undefined : isQueryItem(item) ? 'search' : undefined
  const categoryId =
    'categories' in item ? item.categories.filter(({ primary }) => primary)[0]?.id : undefined
  const distanceLabel =
    'position' in item
      ? getSearchDistanceLabel(countryAndUnit, userCurrentPosition, {
          lat: item.position?.lat,
          lng: item.position?.lng,
        })
      : undefined
  const thumbnail =
    'media' in item && item.media?.images?.items?.length !== undefined
      ? item.media.images.items[0].href
      : undefined
  const { averageRating, reviewCount } =
    'media' in item && item.media?.ratings?.items?.length !== undefined
      ? {
          averageRating: item.media.ratings.items[0].average,
          reviewCount: item.media.ratings.items[0].count,
        }
      : { averageRating: undefined, reviewCount: undefined }
  const address = 'address' in item ? item.address : undefined
  const openingHours = 'openingHours' in item ? item.openingHours : undefined
  const media = 'media' in item ? item.media : undefined
  const contacts = 'contacts' in item ? item.contacts : undefined
  const categories = 'categories' in item ? item.categories : undefined
  const position = 'position' in item ? item.position : undefined
  const distance = 'distance' in item ? item.distance : undefined
  const subtitle = 'address' in item ? item.address.label : undefined
  const serializedPdcId = serializeCompactV0({
    access: position as Position,
    address: address !== undefined ? address.label : '',
    categoryId: categoryId !== undefined ? categoryId : '',
    countryCode: '',
    id: item.id,
    placeType: '',
    politicalView: '',
    position: position as Position,
    title: item.title,
  })
  const urlPath = isQueryType(item.resultType)
    ? `/${mapConfig.url.path.discover}/${encodeURIComponent(item.title)}`
    : `/${mapConfig.url.path.pdc}/${encodeURIComponent(serializedPdcId)}`

  return {
    address,
    averageRating,
    categories,
    categoryId,
    contacts,
    distance,
    distanceLabel,
    iconName,
    id,
    media,
    name: item.title,
    openingHours,
    position,
    resultType: item.resultType,
    reviewCount,
    subtitle,
    thumbnail,
    title: item.title,
    urlPath,
  }
}

export function fromSearchStateListItemToRecentItem(item: SearchStateListItem): RecentItem {
  const placeCategoryId =
    'categories' in item && item.categories && item.categories.length > 0
      ? item.categories.filter((item) => item.primary)[0].id
      : item.categoryId

  return {
    address: item.address?.label,
    distance: item.distance,
    distanceLabel: item.distanceLabel,
    lat: item.position?.lat,
    lon: item.position?.lng,
    placeCategoryId,
    placeId: item.id,
    resultType: item.resultType,
    title: item.title,
  }
}

export const fromSerializedRecentItemToSearchStateListItem = (
  item: SerializedRecentItem,
  countryAndUnit: CountryAndUnit,
  userCurrentPosition: Coordinates | null
): SearchStateListItem => {
  const id = item.placeId
  const categoryId = item.placeCategoryId
  const address = { houseNumber: '', label: item.address ?? '', street: '' }
  const iconName =
    item.type === RecentItemType.freeText ||
    item.type === RecentItemType.categoryQuery ||
    item.type === RecentItemType.chainQuery
      ? 'search'
      : undefined
  const resultType = item.resultType !== undefined ? item.resultType : ''
  const title =
    (item.type === RecentItemType.freeText && `"${item.title.replaceAll('"', '')}"`) || item.title
  const name = item.title.replaceAll('"', '')
  const distanceLabel = getSearchDistanceLabel(countryAndUnit, userCurrentPosition, {
    lat: item.lat,
    lng: item.lon,
  })
  const subtitle = item.address
  const position =
    item.lat !== undefined && item.lon !== undefined ? { lat: item.lat, lng: item.lon } : undefined
  const distance = 'distance' in item ? item.distance : undefined

  const serializedPdcId = serializeCompactV0({
    access: position as Position,
    address: address.label,
    categoryId: categoryId !== undefined ? categoryId : '',
    countryCode: '',
    id: item.placeId,
    placeType: '',
    politicalView: '',
    position: position as Position,
    title: item.title,
  })
  const urlPath =
    resultType === '' || isQueryType(resultType)
      ? `/${mapConfig.url.path.discover}/${encodeURIComponent(name)}`
      : `/${mapConfig.url.path.pdc}/${encodeURIComponent(serializedPdcId)}`

  return {
    address,
    categoryId,
    distance,
    distanceLabel,
    iconName,
    id,
    name,
    position,
    resultType,
    subtitle,
    title,
    urlPath,
  }
}

export const fromSearchResultEntityItemToSearchStateListItem = (
  item: SearchResultEntityItem | SearchStateListItem,
  countryAndUnit: CountryAndUnit,
  userCurrentPosition: Coordinates | null
): SearchStateListItem => {
  const address = item.address
  const id = item.id
  const categoryId =
    'categoryId' in item
      ? item.categoryId
      : 'categories' in item && item.categories
      ? item.categories[0].id
      : undefined
  const distance = 'distance' in item ? item.distance : undefined
  const distanceLabel = getSearchDistanceLabel(countryAndUnit, userCurrentPosition, {
    lat: item.position?.lat,
    lng: item.position?.lng,
  })
  const name = item.title.replaceAll('"', '')
  const resultType = item.resultType
  const title = item.title

  const subtitle = item.address?.label
  const iconName = 'iconName' in item ? item.iconName : undefined
  const position = item.position

  const urlPath =
    item.resultType === '' || isQueryType(item.resultType)
      ? `/${mapConfig.url.path.discover}/${encodeURIComponent(name)}`
      : `/${mapConfig.url.path.pdc}/${id}`

  return {
    address,
    categoryId,
    distance,
    distanceLabel,
    iconName,
    id,
    name,
    position,
    resultType,
    subtitle,
    title,
    urlPath,
  }
}

export function getSearchDistanceLabel(
  countryAndUnit: CountryAndUnit,
  start: { lat?: number; lng?: number } | null | undefined,
  end: { lat?: number; lng?: number } | null | undefined
): string | undefined {
  if (
    start !== null &&
    start !== undefined &&
    start.lat !== null &&
    start.lat !== undefined &&
    start.lng !== null &&
    start.lng !== undefined &&
    end !== null &&
    end !== undefined &&
    end.lat !== null &&
    end.lat !== undefined &&
    end.lng !== null &&
    end.lng !== undefined
  ) {
    const startCoord: Coordinates = { lat: start.lat, lng: start.lng }
    const endCoord: Coordinates = { lat: end.lat, lng: end.lng }
    return calculateAndFormatDistance(startCoord, endCoord, countryAndUnit)
  }
  return undefined
}
